/**
 * apps/web :: lib/retry
 *
 * Tiny exponential-backoff retry helper used by OnboardingWizard's
 * mount-time `getOnboardingStatus()` call (Wave 14e, ROADS-339 / AC #1).
 *
 * Why a separate module rather than inlining inside the wizard?
 *   - Pure functions are testable in isolation with `vi.useFakeTimers()`,
 *     so the timing schedule (1s → 2s → 4s) gets a deterministic
 *     assertion without dragging in React's render lifecycle.
 *   - The same helper can be reused for any other "lightweight read that
 *     occasionally hits a 502 during a deploy" call site.
 *
 * Retry policy (intentional, narrow):
 *   - 5xx ApiError (transient server / proxy failure) → retry.
 *   - Network errors (TypeError thrown by fetch when DNS/TCP fails, or
 *     plain Error subclasses without an HTTP status) → retry.
 *   - 4xx ApiError (404/401/403/429/etc.) → terminal, surface immediately.
 *     A 404 means "no profile" and the caller treats it as data; a 401
 *     bubbles up to ProtectedRoute and triggers a /login redirect — both
 *     would be wrong to retry behind the user's back.
 *
 * Schedule: backoff at 1s, 2s, capped at 4s (3 attempts total by default,
 * matching the AC). Caller can override `maxAttempts`, `baseMs`, `maxMs`
 * — handy for tests.
 *
 * Non-goals: jitter (this is one call per page-load, contention isn't a
 * real problem at MVP scale) and AbortSignal plumbing (the wizard has its
 * own `cancelled` flag for the unmount race; we don't need a second one).
 */

import { ApiError } from "@/lib/api";

export interface RetryOptions {
  /** Max attempts including the initial call. Defaults to 3. */
  maxAttempts?: number;
  /** Initial backoff delay in ms. Defaults to 1000. */
  baseMs?: number;
  /** Hard cap on backoff delay in ms. Defaults to 4000. */
  maxMs?: number;
}

const DEFAULT_OPTS: Required<RetryOptions> = {
  maxAttempts: 3,
  baseMs: 1_000,
  maxMs: 4_000,
};

/**
 * True iff `err` is something we believe a retry could fix:
 *   - 5xx ApiError (server/proxy hiccup); or
 *   - any non-ApiError Error subclass (treated as a network failure).
 *
 * 4xx errors are terminal — see the policy comment above.
 */
export function isRetryableError(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.status >= 500 && err.status < 600;
  }
  return err instanceof Error;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Run `op`, retrying on transient failures with exponential backoff.
 *
 * Resolves with the first successful return value. Rejects with the last
 * error after `maxAttempts` is exhausted (or with the first error if it
 * is not retryable per {@link isRetryableError}).
 */
export async function retryWithBackoff<T>(
  op: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const { maxAttempts, baseMs, maxMs } = { ...DEFAULT_OPTS, ...options };

  let lastErr: unknown = null;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await op();
    } catch (err) {
      lastErr = err;
      // Either the error type is terminal or we've used our last attempt;
      // surface immediately.
      if (!isRetryableError(err) || attempt === maxAttempts - 1) {
        throw err;
      }
      // Exponential: 1s, 2s, 4s, 8s … capped at maxMs. attempt is 0-based.
      const delayMs = Math.min(baseMs * 2 ** attempt, maxMs);
      await sleep(delayMs);
    }
  }
  // Unreachable (the loop always either returns or throws), but TS can't
  // prove the for-loop bound without help.
  throw lastErr;
}
